import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';
import { useAuth } from '../../../contexts/AuthContext';
import UserAvatar from '../../ChallengeWire/UserAvatar';
import SegmentedControl from '../../ChallengeWire/SegmentedControl';
import {
	acceptFriendship,
	createFriendship,
	declineFriendship,
	deleteFriendship,
	fetchFriendships
} from '../../../utils/challengeWireApi';

export default function FriendsPage() {
	const { user, csrfToken, endpoints, activeFitnessProfile } = useAuth();
	const [tab, setTab] = useState('friends');
	const [data, setData] = useState({ friends: [], incoming_pending: [], outgoing_pending: [] });
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(true);
	const [actionError, setActionError] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const load = useCallback(async () => {
		if (!user?.id || !endpoints?.get_user_friendships_endpoint) return;
		setLoading(true);
		try {
			const result = await fetchFriendships({
				userId: user.id,
				endpoint: endpoints.get_user_friendships_endpoint,
				csrfToken
			});
			setData(result);
		} catch (e) {
			setActionError(e.message);
		} finally {
			setLoading(false);
		}
	}, [user?.id, endpoints, csrfToken]);

	useEffect(() => {
		load();
	}, [load]);

	async function handleAddFriend(e) {
		e.preventDefault();
		setSubmitting(true);
		setActionError('');
		try {
			await createFriendship({
				userId: user.id,
				endpoint: endpoints.create_user_friendship_endpoint,
				csrfToken,
				email
			});
			setEmail('');
			await load();
		} catch (err) {
			setActionError(err.message);
		} finally {
			setSubmitting(false);
		}
	}

	async function runAction(fn) {
		setActionError('');
		try {
			await fn();
			await load();
		} catch (err) {
			setActionError(err.message);
		}
	}

	const tabOptions = [
		{ value: 'friends', label: 'All friends', badge: 0 },
		{ value: 'outgoing', label: 'Outgoing', badge: data.outgoing_pending.length },
		{ value: 'incoming', label: 'Incoming', badge: data.incoming_pending.length }
	];

	const list =
		tab === 'incoming'
			? data.incoming_pending
			: tab === 'outgoing'
				? data.outgoing_pending
				: data.friends;

	if (activeFitnessProfile) {
		return (
			<div className="container p-4 pt-4 lg:py-12 md:px-20 max-w-3xl mx-auto">
				<PageTitle title="Friends" />
				<p className="mt-6 text-sm text-gray-600 dark:text-dark-text-secondary">
					Only account owners can add or manage friends. Sign in with the account owner login to invite
					people.
				</p>
			</div>
		);
	}

	return (
		<div className="container p-4 pt-4 lg:py-12 md:px-20 max-w-3xl mx-auto">
			<PageTitle title="Friends" />

			<form onSubmit={handleAddFriend} className="mt-6 flex flex-col sm:flex-row gap-2">
				<input
					type="email"
					required
					placeholder="Friend's email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					className="flex-1 rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg px-3 py-2.5 text-sm text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-secondary"
				/>
				<button
					type="submit"
					disabled={submitting}
					className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
				>
					Add friend
				</button>
			</form>

			<div className="mt-6">
				<SegmentedControl options={tabOptions} value={tab} onChange={setTab} />
			</div>

			{actionError && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{actionError}</p>}

			{loading ? (
				<p className="mt-8 text-sm text-gray-500 dark:text-dark-text-secondary">Loading…</p>
			) : list.length === 0 ? (
				<p className="mt-8 text-sm text-gray-600 dark:text-dark-text-secondary">
					{tab === 'friends' ? 'No friends yet. Add someone by email.' : 'Nothing here.'}
				</p>
			) : (
				<ul className="mt-6 space-y-3">
					{list.map((row) => (
						<li
							key={row.id}
							className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-4"
						>
							<div className="flex items-center gap-3 min-w-0">
								<UserAvatar name={row.user.full_name} />
								<div>
									<div className="font-semibold text-gray-900 dark:text-dark-text">{row.user.full_name}</div>
									<div className="text-sm text-gray-500 dark:text-dark-text-secondary truncate">{row.user.email}</div>
								</div>
							</div>
							<div className="flex flex-wrap gap-2">
								{tab === 'incoming' && (
									<>
										<button
											type="button"
											onClick={() =>
												runAction(() =>
													acceptFriendship({
														userId: user.id,
														endpoint: endpoints.accept_user_friendship_endpoint,
														csrfToken,
														id: row.id
													})
												)
											}
											className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
										>
											Accept
										</button>
										<button
											type="button"
											onClick={() =>
												runAction(() =>
													declineFriendship({
														userId: user.id,
														endpoint: endpoints.decline_user_friendship_endpoint,
														csrfToken,
														id: row.id
													})
												)
											}
											className="rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg"
										>
											Decline
										</button>
									</>
								)}
								{tab === 'friends' && (
									<>
										<Link
											to="/challenge-wire"
											state={{ openSend: true, friendId: row.user.id }}
											className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
										>
											Send challenge
										</Link>
										<button
											type="button"
											onClick={() =>
												runAction(() =>
													deleteFriendship({
														userId: user.id,
														endpoint: endpoints.delete_user_friendship_endpoint,
														csrfToken,
														id: row.id
													})
												)
											}
											className="rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg"
										>
											Unfriend
										</button>
									</>
								)}
								{tab === 'outgoing' && (
									<button
										type="button"
										onClick={() =>
											runAction(() =>
												deleteFriendship({
													userId: user.id,
													endpoint: endpoints.delete_user_friendship_endpoint,
													csrfToken,
													id: row.id
												})
											)
										}
										className="rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg"
									>
										Cancel request
									</button>
								)}
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
